import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

export const PAGE_SIZE_OPTIONS = [25, 50, 75, 100];

export const BOT_DISPOSITION_OPTIONS = [
  { label: 'AI Schedule Bot - Final Expense',       product: 'Final Expense (FEX)' },
  { label: 'AI Schedule Bot - IUL',                 product: 'IUL' },
  { label: 'AI Schedule Bot - Veterans Life',       product: 'Veterans Life' },
  { label: 'AI Schedule Bot - General Life',        product: 'General Life' },
  { label: 'AI Schedule Bot - Mortgage Protection', product: 'Mortgage Protection' },
];

export const ONLY_SHOW_OPTIONS = [
  { group: 'Sale status', items: [
    { value: 'sold',                   label: 'Sold' },
    { value: 'not_sold',               label: 'Not sold' },
    { value: 'quoted',                 label: 'Quoted' },
    { value: 'not_quoted',             label: 'Not quoted' },
  ] },
  { group: 'Activity', items: [
    { value: 'has_been_called',        label: 'Has been called' },
    { value: 'not_called',             label: 'Not called' },
    { value: 'not_called_for_days',    label: 'Not called for X days', companion: 'days' },
    { value: 'not_called_or_texted_today', label: 'Not called or texted today' },
    { value: 'not_texted_for_days',    label: 'Not texted for X days', companion: 'days' },
    { value: 'awaiting_response',      label: 'Awaiting your response' },
    { value: 'has_notes',              label: 'Has notes' },
    { value: 'no_notes',               label: 'No notes' },
  ] },
  { group: 'Contact info', items: [
    { value: 'has_email',              label: 'Has email' },
    { value: 'no_email',               label: 'No email' },
    { value: 'has_birthday',           label: 'Has birthday' },
    { value: 'no_birthday',            label: 'No birthday' },
    { value: 'birthday_today',         label: 'Birthday today' },
    { value: 'birthday_this_month',    label: 'Birthday in month …', companion: 'month' },
    { value: 'birth_year',             label: 'Born in year …', companion: 'year' },
    { value: 'has_zip',                label: 'Has zip' },
    { value: 'no_zip',                 label: 'No zip' },
    { value: 'near_zip',               label: 'Near zip code …', companion: 'zip' },
  ] },
  { group: 'Compliance', items: [
    { value: 'sms_opt_out',            label: 'SMS opt-out' },
    { value: 'not_sms_opt_out',        label: 'Not SMS opt-out' },
    { value: 'on_dnc',                 label: 'On DNC' },
    { value: 'not_on_dnc',             label: 'Not on DNC' },
    { value: 'blocked',                label: 'Blocked leads' },
  ] },
  { group: 'Priority', items: [
    { value: 'hot',                    label: 'Hot 🔥' },
    { value: 'warm',                   label: 'Warm' },
    { value: 'cold',                   label: 'Cold' },
  ] },
];

export const ONLY_SHOW_LOOKUP = ONLY_SHOW_OPTIONS.flatMap((g) => g.items)
  .reduce((acc, it) => { acc[it.value] = it; return acc; }, {});

export function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className={`bg-civix-navy border border-white/10 rounded-2xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-xl'} max-h-[90vh] overflow-auto`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h3 className="font-display text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Input({ label, value, onChange, type = 'text', required }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="input"
      />
    </div>
  );
}

export function Select({ label, value, onChange, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input">
        {children}
      </select>
    </div>
  );
}

export function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQ = false;
      else cur += c;
    } else {
      if (c === '"') inQ = true;
      else if (c === ',') { out.push(cur); cur = ''; }
      else cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

export function useLocalTime(tz) {
  const [now, setNow] = useState(() => formatLocal(tz));
  useEffect(() => {
    const t = setInterval(() => setNow(formatLocal(tz)), 60_000);
    return () => clearInterval(t);
  }, [tz]);
  return now;
}

export function formatLocal(tz) {
  try {
    return new Date().toLocaleTimeString('en-US', {
      timeZone: tz || 'America/Denver',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return ''; }
}
